import {
  AloftArea,
  CraftsmanCostProps,
  ORDER_FEATURES,
  getCraftsmanCost,
  isDefined,
  isUndefined,
} from '@aloft/toolkit-shared';

import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { createFeatureCostSelectors } from '../utils/createFeatureCostSelectors.ts';
import { selectCostProps } from './selectCostProps.ts';

const calculateFinishedBasementAreaCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
) => {
  const improvementsForTransaction = improvements[id];
  const identification = propertyIdentification[id];

  if (!improvementsForTransaction || !identification) return undefined;

  const {
    basement,
    craftsmanQuality: quality,
    propertyType = 'singleFamilyResidence',
  } = improvementsForTransaction;
  const {
    address: { PostalCode: zipCode },
  } = identification;

  if (
    isUndefined(quality) ||
    (quality * 10) % 1 !== 0 ||
    quality < 1 ||
    isUndefined(zipCode) ||
    !ORDER_FEATURES.basement.hasFeature(improvementsForTransaction)
  ) {
    return undefined;
  }

  const calculator = getCraftsmanCost(costProps);

  const finishedArea =
    isDefined(basement) && isDefined(basement.finishedArea)
      ? AloftArea.schema.parse(basement.finishedArea)
      : AloftArea.zero;

  return calculator.getFinishedBasementAreaCost({
    finishedArea,
    quality,
    propertyType,
    zipCode,
  });
};

export const {
  selectFinishedBasementAreaCost,
  selectZipOnlyFinishedBasementAreaCost,
  selectFinishedBasementAreaCosts,
  selectFinishedBasementAreaCostDisplay,
  selectSubjectFinishedBasementAreaCostOutput,
  selectSubjectArvFinishedBasementAreaCostOutput,
} = createFeatureCostSelectors({
  calculate: calculateFinishedBasementAreaCost,
  inputSelectors: [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
  ],
  names: {
    cost: 'selectFinishedBasementAreaCost',
    zipOnly: 'selectZipOnlyFinishedBasementAreaCost',
    costs: 'selectFinishedBasementAreaCosts',
    display: 'selectFinishedBasementAreaCostDisplay',
    subjectOutput: 'selectSubjectFinishedBasementAreaCostOutput',
    subjectArvOutput: 'selectSubjectArvFinishedBasementAreaCostOutput',
  },
});
